/**
 * Kit Inventory Validator
 * 
 * Validates kit availability based on the kit's own inventory,
 * NOT the individual component products' inventory.
 * 
 * Critical: Kits should only be available based on their own inventory level.
 * Component availability should not affect kit availability.
 */

(function (window) {
  'use strict';

  /**
   * Validates if a kit product can be added to cart
   * @param {Object} product - Shopify product object
   * @param {number} requestedQty - Quantity user wants to add
   * @returns {Object} { valid: boolean, error: string|null, available: number }
   */
  function validateKitInventory(product, requestedQty = 1) {
    // Only validate if this is a kit product
    if (!isKitProduct(product)) {
      return { valid: true, error: null, available: Infinity };
    }

    const variant = product.selected_or_first_available_variant || product.variants[0];
    
    if (!variant) {
      return { 
        valid: false, 
        error: 'No variant found for this kit', 
        available: 0 
      };
    }

    // Get kit's own inventory (not component inventory)
    const kitInventory = getKitInventory(variant);

    // Check if requested quantity exceeds available kit inventory
    if (requestedQty > kitInventory) {
      const errorMsg = kitInventory === 0 
        ? 'This kit is sold out'
        : `Only ${kitInventory} kit${kitInventory > 1 ? 's' : ''} available`;
      
      return {
        valid: false,
        error: errorMsg,
        available: kitInventory
      };
    }

    return {
      valid: true,
      error: null,
      available: kitInventory
    };
  }

  /**
   * Checks if product is a kit (bundle)
   * @param {Object} product - Shopify product object
   * @returns {boolean}
   */
  function isKitProduct(product) {
    if (!product) return false;

    // Check for Simple Bundles & Kits metafields
    const hasFixedBundle = product.metafields?.bundle?.fixed_bundle_products;
    const hasCustomBundle = product.metafields?.bundle?.custom_bundle_products;
    
    // Check for kit tags
    const hasKitTag = product.tags?.some(tag => 
      tag.toLowerCase().includes('kit') || 
      tag.toLowerCase().includes('bundle')
    );

    return !!(hasFixedBundle || hasCustomBundle || hasKitTag);
  }

  /**
   * Gets the kit's own inventory (not component inventory)
   * @param {Object} variant - Variant object
   * @returns {number} Available inventory for the kit itself
   */
  function getKitInventory(variant) {
    if (!variant) return 0;

    // For kits, inventory_quantity represents the kit's own inventory
    // This is the manually set inventory that should be respected
    const inventory = variant.inventory_quantity || 0;
    
    // If inventory_management is null, treat as unlimited
    if (!variant.inventory_management) {
      return Infinity;
    }

    // If inventory_policy is 'continue', allow overselling
    if (variant.inventory_policy === 'continue') {
      return Infinity;
    }

    return Math.max(0, inventory);
  }

  /**
   * Displays error message to user
   * @param {string} message - Error message
   * @param {HTMLElement} container - Container element for error
   */
  function showKitError(message, container) {
    if (!container) {
      // Fallback to alert if no container
      alert(message);
      return;
    }

    container.innerHTML = `
      <div class="form-error form-error--visible" style="color: #d02e2e; margin: 1rem 0;">
        <span>${message}</span>
      </div>
    `;
    container.style.display = 'block';

    // Auto-hide after 5 seconds
    setTimeout(() => {
      container.innerHTML = '';
      container.style.display = 'none';
    }, 5000);
  }

  /**
   * Clears error messages
   * @param {HTMLElement} container - Container element for error
   */
  function clearKitError(container) {
    if (container) {
      container.innerHTML = '';
      container.style.display = 'none';
    }
  }

  /**
   * Updates max quantity input based on kit inventory
   * @param {Object} product - Shopify product object
   * @param {HTMLElement} quantityInput - Quantity input element
   */
  function updateKitQuantityLimit(product, quantityInput) {
    if (!quantityInput || !isKitProduct(product)) return;

    const variant = product.selected_or_first_available_variant || product.variants[0];
    const kitInventory = getKitInventory(variant);

    if (kitInventory !== Infinity) {
      quantityInput.setAttribute('max', kitInventory);
      
      // If current value exceeds available, reset to max
      const currentValue = parseInt(quantityInput.value, 10);
      if (currentValue > kitInventory) {
        quantityInput.value = kitInventory;
      }
    }
  }

  /**
   * Validates cart line item quantity update for kits
   * Fetches cart data and checks if requested quantity exceeds kit inventory
   * @param {HTMLElement} input - Quantity input element
   * @param {number} requestedQty - Requested quantity
   * @returns {Promise<Object>} { valid: boolean, error: string|null, maxAllowed: number }
   */
  async function validateCartKitQuantity(input, requestedQty) {
    try {
      // Get cart data
      const response = await fetch(`${window.theme.routes.cart}.js`);
      const cart = await response.json();
      
      // Find the line item from the input's data attribute
      const updateKey = input.getAttribute('data-update-cart');
      if (!updateKey) {
        return { valid: true, error: null, maxAllowed: Infinity };
      }
      
      const lineItem = cart.items.find(item => item.key === updateKey);
      if (!lineItem) {
        return { valid: true, error: null, maxAllowed: Infinity };
      }
      
      // Check if this is a kit
      const hasFixedBundle = lineItem.product?.metafields?.bundle?.fixed_bundle_products;
      const hasCustomBundle = lineItem.product?.metafields?.bundle?.custom_bundle_products;
      const hasSetProperty = lineItem.properties?._set;
      const isKit = !!(hasFixedBundle || hasCustomBundle || hasSetProperty);
      
      if (!isKit) {
        return { valid: true, error: null, maxAllowed: Infinity };
      }
      
      // For kits, check the product's first variant inventory (kit's own inventory)
      const kitInventory = lineItem.product?.variants?.[0]?.inventory_quantity || 0;
      
      if (requestedQty > kitInventory) {
        const errorMsg = kitInventory === 0 
          ? 'Este kit está agotado'
          : `Solo ${kitInventory} kit${kitInventory > 1 ? 's' : ''} disponible${kitInventory > 1 ? 's' : ''}`;
        
        return {
          valid: false,
          error: errorMsg,
          maxAllowed: kitInventory
        };
      }
      
      return {
        valid: true,
        error: null,
        maxAllowed: kitInventory
      };
    } catch (error) {
      console.error('Kit cart validation error:', error);
      return { valid: true, error: null, maxAllowed: Infinity };
    }
  }

  // Expose API
  window.KitInventoryValidator = {
    validate: validateKitInventory,
    validateCartQuantity: validateCartKitQuantity,
    isKit: isKitProduct,
    getInventory: getKitInventory,
    showError: showKitError,
    clearError: clearKitError,
    updateQuantityLimit: updateKitQuantityLimit
  };

})(window);
